import { useCallback } from 'react';

import { productInfoService } from '@/features/Product/api';
import type {
  CreateProductPayload,
  CreateProductPayloadDTO,
  CreateProductResponseDTO,
} from '@/features/Product/api/dto';
import useProductStore from '@/features/Product/store/ProductStore';
import { filterMap } from '@/utils/lazy';
import { isEmptyString } from '@/utils/util';

export const useCreateProducts = () => {
  const { addProduct, addBrand, addLine, productMap, lineMap, brandMap } =
    useProductStore(
      ({ addProduct, addBrand, addLine, productMap, lineMap, brandMap }) => ({
        addProduct,
        addBrand,
        addLine,
        productMap,
        lineMap,
        brandMap,
      }),
    );

  const isValidProduct = useCallback(
    (payload: CreateProductPayload): CreateProductPayload | null => {
      const product = productMap
        .values()
        .find((product) => product.product_name === payload.product_name);

      // 하나라도 존재하지 않는 경우 새로운 product로 생성
      if (!product || !product.line_id || !product.brand_id) return payload;

      const line = lineMap.get(product.line_id);
      const brand = brandMap.get(product.brand_id);

      // brand와 line이 모두 같은 product가 존재하는 경우 중복 생성 방지
      if (
        payload.product_brand_name === brand?.name &&
        payload.product_line_name === line?.name
      )
        return null;

      return payload;
    },
    [productMap, lineMap, brandMap],
  );

  const updateProductStore = useCallback(
    (response: CreateProductResponseDTO) => {
      response.created_products.forEach((product) => {
        addProduct(product);
      });
      response.created_product_brands.forEach((brand) => {
        addBrand(brand);
      });
      response.created_product_lines.forEach((line) => {
        addLine(line);
      });
    },
    [addProduct, addBrand, addLine],
  );

  const createProduct = useCallback(
    async (values: Partial<CreateProductPayload>) => {
      const payload = checkEmptyValue(values);
      if (!payload) return;
      if (!isValidProduct(payload)) return;
      const payloads: CreateProductPayloadDTO = [payload];
      const res = await productInfoService.createProducts(payloads);
      updateProductStore(res);
    },
    [isValidProduct, updateProductStore],
  );

  const createMultipleProducts = useCallback(
    async (values: Partial<CreateProductPayload>[]) => {
      const payloads = filterMap(values, (value) => {
        const payload = checkEmptyValue(value);
        if (!payload) return null;
        if (!isValidProduct(payload)) return null;
        return payload;
      });
      const res = await productInfoService.createProducts(payloads);
      updateProductStore(res);
    },
    [updateProductStore, isValidProduct],
  );

  return { createProduct, createMultipleProducts };
};

const checkEmptyValue = (
  values: Partial<CreateProductPayload>,
): CreateProductPayload | null => {
  if (!values.product_name || isEmptyString(values.product_name)) return null;
  if (!values.product_brand_name || isEmptyString(values.product_brand_name))
    return null;
  if (!values.product_line_name || isEmptyString(values.product_line_name))
    return null;
  return {
    product_name: values.product_name,
    product_brand_name: values.product_brand_name,
    product_line_name: values.product_line_name,
  };
};
